// Phase 0b: TanStack Query bindings for the Pre-Award workspace. The
// orchestration RPCs live on zou's ProjectDataService (one round-trip per
// user action) — there is no separate Pre-Award proto service to wire up.
import { IntelligenceTypes } from '@mono/proto-clients';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { projectDataClient } from '../../../lib/connect/clients';
const PRE_AWARD_KEY = 'pre-award';
export function awardRunWorking(run) {
    return !!run && [
        IntelligenceTypes.IntelligenceRunState.QUEUED,
        IntelligenceTypes.IntelligenceRunState.EXTRACTING,
        IntelligenceTypes.IntelligenceRunState.RECONCILING
    ].includes(run.state);
}
export function useAwardComparisonRun(preAwardProjectId) {
    const queryClient = useQueryClient();
    const queryKey = [
        'award-comparison-run',
        preAwardProjectId
    ];
    const query = useQuery({
        queryKey,
        enabled: !!preAwardProjectId,
        queryFn: async ()=>{
            const response = await projectDataClient.getAwardComparisonRun({
                preAwardProjectId
            });
            return {
                run: response.run ?? null,
                lastCompletedChangeSetId: response.lastCompletedChangeSetId
            };
        },
        refetchInterval: (q)=>awardRunWorking(q.state.data?.run) ? 2000 : false
    });
    const updateRun = (run)=>{
        queryClient.setQueryData(queryKey, (previous)=>({
                run,
                lastCompletedChangeSetId: previous?.lastCompletedChangeSetId ?? ''
            }));
        void queryClient.invalidateQueries({
            queryKey
        });
    };
    const start = useMutation({
        mutationFn: async (args)=>{
            const response = await projectDataClient.startAwardComparison({
                preAwardProjectId,
                ...args
            });
            if (!response.run) throw new Error('The extraction run was not created');
            return response.run;
        },
        onSuccess: updateRun,
        onError: ()=>{
            void queryClient.invalidateQueries({
                queryKey
            });
        }
    });
    const cancel = useMutation({
        mutationFn: async (runId)=>(await projectDataClient.cancelAwardComparisonRun({
                preAwardProjectId,
                runId
            })).run ?? null,
        onSuccess: updateRun
    });
    return {
        ...query,
        data: query.data?.run ?? null,
        lastCompletedChangeSetId: query.data?.lastCompletedChangeSetId ?? '',
        start,
        cancel
    };
}
/**
 * Load the Pre-Award workspace: PreAwardProject row + all 8 readiness
 * rows in one round-trip.
 */ export function usePreAwardWorkspace(preAwardProjectId) {
    return useQuery({
        queryKey: [
            PRE_AWARD_KEY,
            preAwardProjectId
        ],
        enabled: !!preAwardProjectId,
        queryFn: async ()=>{
            const resp = await projectDataClient.getPreAwardWorkspace({
                preAwardProjectId
            });
            if (!resp.summary) throw new Error('GetPreAwardWorkspace returned no summary');
            return resp.summary;
        }
    });
}
/**
 * Upsert one readiness row. Pangaea is keyed by (pre_award_project_id,
 * area) so re-calling with the same status is a no-op at the data plane
 * but still emits an audit row (per gap-analysis convention).
 *
 * `workspaceCorrelationId` (Phase 0b F-D) threads one correlation across
 * every readiness save in a single workspace session. Zou's
 * UpsertPreAwardReadiness handler reads the X-Workspace-Correlation
 * header and groups the 8 area upserts under one audit row, so the
 * timeline tells the "operator walked the page" story as one event
 * rather than eight unrelated ones. Pass undefined to fall back to a
 * fresh per-call correlation (direct-API callers / tests).
 */ export function useUpsertPreAwardReadiness(workspaceCorrelationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ preAwardProjectId, area, status, dataJson })=>{
            const resp = await projectDataClient.upsertPreAwardReadiness({
                preAwardProjectId,
                area,
                status,
                dataJson: dataJson ?? ''
            }, workspaceCorrelationId ? {
                headers: {
                    'X-Workspace-Correlation': workspaceCorrelationId
                }
            } : undefined);
            return resp;
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    PRE_AWARD_KEY,
                    vars.preAwardProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to update readiness');
        }
    });
}
/**
 * Load the frozen bid baseline behind a pre-award workspace. Zou proxies
 * pangaea's snapshot (GetBidBaseline) and resolves the baseline id from the
 * pre-award row, so callers pass only the pre-award id. Read-only — the
 * baseline is immutable once frozen at conversion.
 */ export function useBidBaseline(preAwardProjectId, enabled = true) {
    return useQuery({
        queryKey: [
            PRE_AWARD_KEY,
            preAwardProjectId,
            'bid-baseline'
        ],
        enabled: enabled && !!preAwardProjectId,
        queryFn: async ()=>{
            const resp = await projectDataClient.getBidBaseline({
                preAwardProjectId
            });
            if (!resp.baseline) throw new Error('GetBidBaseline returned no baseline');
            return resp.baseline;
        }
    });
}
/**
 * Kick off the SDS §4 comparison engine. Returns the change-set summary
 * the caller routes to (`/delivery/pre-award/:id/comparison/:csId`).
 */ export function useBeginAwardComparison() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ preAwardProjectId, awardDocId, awardArtifactIds, awardedContract })=>{
            const resp = await projectDataClient.beginAwardComparison({
                preAwardProjectId,
                awardDocId: awardDocId ?? '',
                awardArtifactIds: awardArtifactIds ?? [],
                awardedContract
            });
            if (!resp.summary) throw new Error('BeginAwardComparison returned no summary');
            return resp.summary;
        },
        onSuccess: (_summary, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    PRE_AWARD_KEY,
                    vars.preAwardProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to begin comparison');
        }
    });
}
